<script setup lang="ts">
import { ITicket } from '@/features/tickets/types'
import { mdiMinus, mdiPlus } from '@mdi/js'
import { ref, watch } from 'vue'
import { centsToDollars } from '@/services/currency'

const props = defineProps<{
  ticket: ITicket
  checkboxMode?: boolean
  currentQuantity?: number
}>()

const emit = defineEmits<{
  (e: 'update:quantity', ticketId: number, quantity: number): void
}>()

const quantity = ref(props.currentQuantity || 0)

watch(
  () => props.currentQuantity,
  (newQuantity) => {
    quantity.value = newQuantity || 0
  }
)

const increment = () => {
  if (props.checkboxMode) return
  if (props.ticket.remaining === quantity.value) return
  quantity.value += 1
  emit('update:quantity', props.ticket.id, quantity.value)
}

const decrement = () => {
  if (props.checkboxMode) return
  if (quantity.value === 0) return
  quantity.value -= 1
  emit('update:quantity', props.ticket.id, quantity.value)
}

const toggleCheckbox = () => {
  if (!props.checkboxMode) return
  quantity.value = quantity.value === 0 ? 1 : 0
  emit('update:quantity', props.ticket.id, quantity.value)
}
</script>

<template>
  <div class="ticket-item">
    <div class="ticket-info">
      <div>
        <h3 class="ticket-title">{{ ticket.title }}</h3>
        <p
          v-if="ticket.isRunOut"
          class="run-out-message"
        >
          ({{ ticket.remaining }} tickets remaining)
        </p>
      </div>
      <p class="ticket-price-info">
        <span class="ticket-price">{{ centsToDollars(ticket.priceWithDiscount ?? ticket.price) }}</span>
        <span
          v-if="'priceWithDiscount' in ticket"
          class="old-price"
        >
          {{ centsToDollars(ticket.price) }}
        </span>
      </p>
    </div>
    <div class="ticket-counter">
      <template v-if="checkboxMode">
        <v-checkbox
          :input-value="quantity > 0"
          color="var(--color-blue-800)"
          @change="toggleCheckbox"
        />
      </template>
      <template v-else>
        <v-btn
          color="var(--color-blue-800)"
          fab
          x-small
          depressed
          :disabled="quantity === 0"
          @click="decrement"
        >
          <v-icon color="white">{{ mdiMinus }}</v-icon>
        </v-btn>
        <span class="quantity">{{ quantity }}</span>
        <v-btn
          color="var(--color-blue-800)"
          fab
          x-small
          depressed
          :disabled="ticket.remaining !== null && quantity >= ticket.remaining"
          @click="increment"
        >
          <v-icon color="white">{{ mdiPlus }}</v-icon>
        </v-btn>
      </template>
      <p
        v-if="quantity >= ticket.remaining"
        class="error-message"
      >
        Sorry, You can't buy more than {{ ticket.remaining }} tickets.
      </p>
    </div>
    <p
      v-if="ticket.discountTitle"
      class="discount-title"
    >
      {{ ticket.discountTitle }}
    </p>
    <p
      v-if="ticket.description"
      class="ticket-description"
    >
      {{ ticket.description }}
    </p>
  </div>
</template>

<style scoped lang="scss">
.ticket-item {
  display: grid;
  grid-template-areas:
    'info counter'
    'discount discount'
    'description description';
  grid-template-columns: 1fr auto;
  gap: 8px 12px;
  padding: 12px;
  border: 1px solid transparent;
  border-radius: 6px;
  box-shadow: 0 1px 4px 0 rgb(0 0 0 / 20%);

  .ticket-info {
    grid-area: info;
    display: flex;
    flex-direction: column;
    gap: 8px;

    .ticket-title {
      font-size: 24px;
      line-height: 30px;
      font-weight: 700;
      color: var(--color-dark-800);
    }

    .run-out-message {
      font-size: 14px;
      line-height: 20px;
      color: var(--error-red);
    }

    .ticket-price-info {
      font-size: 16px;
      line-height: 24px;
      color: var(--color-dark-800);

      .ticket-price {
        margin-right: 12px;
        font-weight: 700;
      }

      .old-price {
        text-decoration: line-through;
      }
    }
  }

  .discount-title {
    grid-area: discount;
    font-size: 16px;
    line-height: 24px;
    color: var(--color-dark-800);
  }

  .ticket-description {
    grid-area: description;
    font-size: 14px;
    line-height: 20px;
    color: var(--color-grey-300);
  }

  .ticket-counter {
    position: relative;
    grid-area: counter;
    display: flex;
    align-self: start;
    align-items: center;
    gap: 16px;

    .quantity {
      text-align: center;
      font-size: 20px;
      line-height: 1.3;
      color: var(--color-dark-800);
    }

    .error-message {
      position: absolute;
      top: calc(100% + 8px);
      right: 0;
      left: auto;
      width: fit-content;
      font-size: 12px;
      line-height: 16px;
      color: var(--error-red);
    }
  }

  p {
    margin: 0;
  }
}
</style>
